import { ChevronRight, Factory } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { StatusBadge } from "@/components/status-badge"
import { Card } from "@/components/ui/card"
import type { MillFloorUsinage } from "@/features/mill-floor/api/types"
import {
  usinageActualLots,
  usinageInputKg,
  usinageOutputKg,
} from "@/features/mill-floor/lib/load-math"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

interface PastUsinagesCardProps {
  runs: MillFloorUsinage[]
}

export function PastUsinagesCard({ runs }: PastUsinagesCardProps) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  if (runs.length === 0) return null

  const sorted = [...runs].sort((a, b) => {
    const aT = a.endDate ?? a.startDate
    const bT = b.endDate ?? b.startDate
    return aT < bT ? 1 : -1
  })

  const totalMT = sorted.reduce(
    (acc, r) => acc + kgToMt(usinageInputKg(r)),
    0,
  )
  const totalLots = sorted.reduce((acc, r) => acc + usinageActualLots(r), 0)

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
            {t("millFloor.past.eyebrow")}
          </p>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("millFloor.past.title")}
          </h2>
        </div>
        <div className="text-muted-foreground text-right text-xs">
          <div>
            <span className="text-foreground mr-1 tabular-nums">
              {sorted.length}
            </span>
            {t(
              sorted.length === 1
                ? "millFloor.past.unitSingular"
                : "millFloor.past.unitPlural",
            )}
          </div>
          {totalMT > 0 ? (
            <div className="mt-0.5 text-[10px] tabular-nums">
              {t("millFloor.past.mtLots", {
                mt: formatNumber(totalMT, 1),
                lots: totalLots,
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-md border">
        {sorted.map((run, i) => {
          const inputKg = usinageInputKg(run)
          const outputKg = usinageOutputKg(run)
          const lossKg = Math.max(0, inputKg - outputKg)
          const lossPct = inputKg > 0 ? (lossKg / inputKg) * 100 : 0
          const inputMT = kgToMt(inputKg)
          return (
            <button
              key={run.id}
              type="button"
              onClick={() => navigate(paths.usinageDetail(run.id))}
              className="hover:bg-muted/40 flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors"
              style={{
                borderBottom:
                  i === sorted.length - 1
                    ? undefined
                    : "1px solid var(--border)",
              }}
            >
              <span className="bg-muted text-muted-foreground flex size-6 flex-shrink-0 items-center justify-center rounded-full">
                <Factory className="size-3" />
              </span>
              <div className="min-w-0 w-40">
                <div className="truncate font-mono text-xs">
                  {run.usinageNumber}
                </div>
                <div className="text-muted-foreground truncate text-[10px]">
                  {String(run.startDate).slice(0, 10)} →{" "}
                  {run.endDate ? String(run.endDate).slice(0, 10) : "—"}
                </div>
              </div>
              <div className="text-muted-foreground flex-1 truncate text-xs">
                {run.operator}
              </div>
              <StatusBadge
                tone={run.status === "COMPLETED" ? "success" : "info"}
              >
                {t(
                  run.status === "COMPLETED"
                    ? "detail.runStatus.Completed"
                    : "detail.runStatus.Running",
                )}
              </StatusBadge>
              <span className="text-muted-foreground w-20 text-right text-xs tabular-nums">
                {t("millFloor.past.lossPct", { pct: formatNumber(lossPct, 1) })}
              </span>
              <span className="w-28 text-right text-xs tabular-nums">
                {t("millFloor.past.rowMtLots", {
                  mt: formatNumber(inputMT, 1),
                  lots: usinageActualLots(run),
                })}
              </span>
              <ChevronRight className="text-muted-foreground size-4 flex-shrink-0" />
            </button>
          )
        })}
      </div>
    </Card>
  )
}
